import { PLACEHOLDER_EXPERIENCE } from '../aggregator-client-context';
import { useSearchSessionId } from '../../common/search-session-provider';
import { useTypedAggregatorClient } from '../aggregator-client-context/aggregator-client-context';
import { SITE_FILTER_TYPE_NAME } from '../filters';
const MAX_DEFAULT_SITES = 3;

/**
 * A definition of a {@link Section} in the Search Platform which is backed by
 * xpsearch-aggregator. Enforces the section to have a scope and a more
 * strongly typed resultMapper for transforming results for the specified scope
 * into a common format.
 */

export const createSearchSupplier = (aggregatorClient, sections, context, experience) => async ({
  query,
  filters: allFilters,
  workspaces,
  sectionIds: allPermissionedScopes
}) => {
  const defaultSites = workspaces === null || workspaces === void 0 ? void 0 : workspaces.slice(0, MAX_DEFAULT_SITES).map(workspace => workspace.id);

  // Extract sites from filters
  const siteFilter = allFilters.find(filter => filter['@type'] === SITE_FILTER_TYPE_NAME);
  // @ts-ignore - typescript is not smart enough to know `siteFilter` will only ever be the sites filter
  const sites = (siteFilter === null || siteFilter === void 0 ? void 0 : siteFilter.values) || defaultSites || [];

  // Remove sites from the filters array
  const filters = allFilters.filter(filter => filter['@type'] !== SITE_FILTER_TYPE_NAME).map(f => f);
  const permittedSections = sections.filter(section => allPermissionedScopes.indexOf(section.id) >= 0).filter(section => section.isEnabled ? section.isEnabled(query, filters) : true);
  const scopes = permittedSections.map(section => section.scope);
  const {
    response
  } = await aggregatorClient.search({
    query,
    scopes,
    modelParams: [],
    experience,
    context,
    filters,
    sites
  });
  const sectionsWithResults = permittedSections.map(({
    scope,
    id,
    title,
    resultMapper,
    ...rest
  }) => {
    const results = response.retrieveScope(scope);
    if (results === null) {
      throw new Error(`Scope ${scope} was not found in the response, despite being requested`);
    }
    return {
      ...rest,
      id,
      title,
      size: results.size,
      searchResults: resultMapper(results) || [],
      resultMapper
    };
  });
  const scopesWithSize = response.rawData.scopes.filter(({
    size
  }) => size);
  const reponseHasSize = scopesWithSize.length > 0;
  const totalSizeAsReportedByAPI = reponseHasSize ? response.rawData.scopes.reduce((count, scope) => count + (scope.size || 0), 0) : undefined;
  return {
    size: totalSizeAsReportedByAPI,
    sections: sectionsWithResults
  };
};

/**
 * Returns a {@link ResultSuppliers} object containing suppliers for pre and post query which
 * are backed by the search platform.
 *
 * The suppliers consider registered scopes, that is scopes that have had permissions evaluated for. I.e,
 * these suppliers will not request scopes that the {@link SearchDialogProduct} permission supplier has
 * returned no permission for.
 *
 * @param id The product id to generate suppliers for
 * @param sections the sections in the search dialog to generate suppliers for. These sections are returned in each supplier,
 * with results from the search platform attached to them.
 * @param overrides to override the item suppliers with input props
 * @returns a {@link ResultSuppliers} object containing a pre and post query supplier
 */
export const useDefaultSuppliers = (id, sections, overrides) => {
  var _overrides$preQueryIt, _overrides$postQueryI;
  const aggregatorClient = useTypedAggregatorClient();
  const sessionId = useSearchSessionId();
  const search = createSearchSupplier(aggregatorClient, sections, {
    sessionId,
    referrerId: ''
  }, PLACEHOLDER_EXPERIENCE);
  return {
    preQueryItemSupplier: (_overrides$preQueryIt = overrides === null || overrides === void 0 ? void 0 : overrides.preQueryItemSupplier) !== null && _overrides$preQueryIt !== void 0 ? _overrides$preQueryIt : args => search({
      ...args,
      query: '',
      filters: []
    }),
    postQueryItemSupplier: (_overrides$postQueryI = overrides === null || overrides === void 0 ? void 0 : overrides.postQueryItemSupplier) !== null && _overrides$postQueryI !== void 0 ? _overrides$postQueryI : search
  };
};